import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// State to ZIP code mapping for major cities
const STATE_ZIP_MAP = {
  'California': { city: 'Los Angeles', state: 'CA', zip: '90001' },
  'New York': { city: 'New York', state: 'NY', zip: '10001' },
  'Texas': { city: 'Houston', state: 'TX', zip: '77001' },
  'Florida': { city: 'Miami', state: 'FL', zip: '33101' }
};

/**
 * Route Selection Page
 * Allows users to select their shipping route and view pricing
 */
function SelectRoute() {
  const navigate = useNavigate();
  const [fromCountry, setFromCountry] = useState('United States');
  const [toCountry, setToCountry] = useState('United States');
  const [fromState, setFromState] = useState('California');
  const [toState, setToState] = useState('New York');
  const [serviceType, setServiceType] = useState('one-way'); // 'one-way' or 'return'
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState({
    ground: { '23': 71, '50': 88, '70': 105 },
    express: { '23': 151, '50': 214, '70': 298 }
  });

  // Fetch UPS rates when states change
  useEffect(() => {
    const fetchRates = async () => {
      setLoading(true);
      try {
        // For same-state shipping, use different cities within the state
        const STATE_ALTERNATE_ZIP_MAP = {
          'California': { city: 'San Francisco', state: 'CA', zip: '94102' },
          'New York': { city: 'Buffalo', state: 'NY', zip: '14201' },
          'Texas': { city: 'Dallas', state: 'TX', zip: '75201' },
          'Florida': { city: 'Jacksonville', state: 'FL', zip: '32099' }
        };

        const fromInfo = STATE_ZIP_MAP[fromState];
        const toInfo = fromState === toState 
          ? STATE_ALTERNATE_ZIP_MAP[toState]  // Use alternate city for same-state
          : STATE_ZIP_MAP[toState];
        
        // Fetch rates for different weights
        const weights = ['23', '50', '70'];
        const groundRates = {};
        const expressRates = {};
        
        for (const weight of weights) {
          const response = await axios.get('/api/bookings/ups-rates', {
            params: {
              fromCity: fromInfo.city,
              fromState: fromInfo.state,
              fromZip: fromInfo.zip,
              toCity: toInfo.city,
              toState: toInfo.state,
              toZip: toInfo.zip,
              weight: weight
            }
          });
          
          if (response.data.success && response.data.rates) {
            // Find Ground (03) and 3 Day Select (12) rates
            const groundRate = response.data.rates.find(r => r.Service.Code === '03');
            const expressRate = response.data.rates.find(r => r.Service.Code === '12');
            
            if (groundRate) {
              groundRates[weight] = Math.ceil(parseFloat(groundRate.TotalCharges.MonetaryValue));
            }
            if (expressRate) {
              expressRates[weight] = Math.ceil(parseFloat(expressRate.TotalCharges.MonetaryValue));
            }
          }
        }
        
        setRates({
          ground: groundRates,
          express: expressRates
        });
      } catch (error) {
        console.error('Error fetching UPS rates:', error);
        // Keep default rates on error
      } finally {
        setLoading(false);
      }
    };
    
    fetchRates();
  }, [fromState, toState]);

  const handleBookStandard = () => {
    navigate('/book', { 
      state: { 
        serviceLevel: 'standard',
        fromCountry,
        toCountry,
        fromState,
        toState,
        showAddItemsPage: true
      } 
    });
  };

  const handleBookExpress = () => {
    navigate('/book', { 
      state: { 
        serviceLevel: 'express',
        fromCountry,
        toCountry,
        fromState,
        toState,
        showAddItemsPage: true
      } 
    });
  };

  return (
    <div className="bg-light min-vh-100">
      <Container className="py-5">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active">Route Prices</li>
          </ol>
        </nav>

        {/* Your Route Section */}
        <div className="text-center mb-5">
          <h1 className="mb-3">Your Route</h1>
          <p className="text-muted">
            Includes 24-hour support, free cover and the<br />
            confidence of over 100,000 reviews with a 4.8/5 star average.
          </p>
        </div>

        {/* Route Selection Form */}
        <Row className="justify-content-center mb-5">
          <Col lg={8}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4">
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold">From</Form.Label>
                      <Form.Select 
                        value={fromState}
                        onChange={(e) => setFromState(e.target.value)}
                      >
                        <option>California</option>
                        <option>New York</option>
                        <option>Texas</option>
                        <option>Florida</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label className="fw-bold">To</Form.Label>
                      <Form.Select 
                        value={toState}
                        onChange={(e) => setToState(e.target.value)}
                      >
                        <option>New York</option>
                        <option>California</option>
                        <option>Texas</option>
                        <option>Florida</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <p className="text-center text-muted small mb-0">
                  To get your quote please select the collection and destination states
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Service Type Toggle */}
        <div className="text-center mb-4">
          <div className="btn-group" role="group">
            <Button 
              variant={serviceType === 'one-way' ? 'success' : 'outline-secondary'}
              onClick={() => setServiceType('one-way')}
              className="px-4"
            >
              One way Prices
            </Button>
            <Button 
              variant={serviceType === 'return' ? 'success' : 'outline-secondary'}
              onClick={() => setServiceType('return')}
              className="px-4"
            >
              Return Prices
            </Button>
          </div>
          <p className="text-muted small mt-3 mb-0">
            <i className="fas fa-info-circle me-1"></i>
            Prices shown are estimates. Final pricing will be calculated based on your actual pickup and delivery addresses.
          </p>
        </div>

        {/* Pricing Cards */}
        <Row className="justify-content-center mb-5">
          <Col lg={10}>
            <Row>
              {/* Standard Service */}
              <Col md={6} className="mb-4">
                <Card className="h-100 shadow-sm border-primary">
                  <Card.Body className="text-center p-4">
                    <h3 className="mb-2">Standard</h3>
                    <p className="text-muted mb-4">5-7 Working Days</p>
                    
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading rates...</span>
                        </Spinner>
                        <p className="text-muted mt-2">Fetching real-time rates...</p>
                      </div>
                    ) : (
                      <>
                        <div className="mb-3 text-start">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="align-self-end">Up to 23lb</span>
                            <div className="text-end">
                              <small className="text-muted d-block" style={{fontSize: '0.75rem', lineHeight: '1'}}>Price per item</small>
                              <strong className="d-block">${rates.ground['23'] || 71}.00</strong>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Up to 50lb</span>
                            <strong>${rates.ground['50'] || 88}.00</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-4">
                            <span>Up to 70lb</span>
                            <strong>${rates.ground['70'] || 105}.00</strong>
                          </div>
                        </div>

                        <Button 
                          variant="success" 
                          className="w-100 py-2"
                          onClick={handleBookStandard}
                        >
                          Book Standard
                        </Button>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Express Service */}
              <Col md={6} className="mb-4">
                <Card className="h-100 shadow-sm border-success">
                  <Card.Body className="text-center p-4">
                    <h3 className="mb-2">Express</h3>
                    <p className="text-muted mb-4">3 Working Days</p>
                    
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading rates...</span>
                        </Spinner>
                        <p className="text-muted mt-2">Fetching real-time rates...</p>
                      </div>
                    ) : (
                      <>
                        <div className="mb-3 text-start">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="align-self-end">Up to 23lb</span>
                            <div className="text-end">
                              <small className="text-muted d-block" style={{fontSize: '0.75rem', lineHeight: '1'}}>Price per item</small>
                              <strong className="d-block">${rates.express['23'] || 151}.00</strong>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Up to 50lb</span>
                            <strong>${rates.express['50'] || 214}.00</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-4">
                            <span>Up to 70lb</span>
                            <strong>${rates.express['70'] || 298}.00</strong>
                          </div>
                        </div>

                        <Button 
                          variant="outline-success" 
                          className="w-100 py-2"
                          onClick={handleBookExpress}
                        >
                          Book Express
                        </Button>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <p className="text-center text-muted small">
              Quotes are based on size and weight.{' '}
              <a href="/pricing-info">find out more about our generous size allowances.</a>
            </p>
          </Col>
        </Row>

        {/* Route Alerts */}
        <Row className="justify-content-center mb-5">
          <Col lg={10}>
            <Alert variant="warning" className="border-warning">
              <h5 className="alert-heading">
                <i className="fas fa-exclamation-circle me-2"></i>
                United States Route Alerts
              </h5>
              <p className="mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Bookings need to be placed prior to 20:00ET for collection requests for the following day.
              </p>
            </Alert>
          </Col>
        </Row>

        {/* What You Can Send */}
        <section className="mb-5">
          <h2 className="text-center mb-5">What You Can Send</h2>
          <Row className="g-4">
            <Col md={4}>
              <Card className="h-100 text-center border-0 bg-light">
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <i className="fas fa-suitcase fa-4x text-primary"></i>
                  </div>
                  <h5 className="mb-3">Suitcases & Bags</h5>
                  <p className="text-muted mb-3">
                    We ship luggage to over 100 countries worldwide each month.
                  </p>
                  <a href="#" className="text-decoration-none fw-bold">Show More</a>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 text-center border-0 bg-light">
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <i className="fas fa-box fa-4x text-primary"></i>
                  </div>
                  <h5 className="mb-3">Boxes</h5>
                  <p className="text-muted mb-3">
                    Yes! We send boxes too, but only use sturdy double walled cardboard boxes.
                  </p>
                  <a href="#" className="text-decoration-none fw-bold">Show More</a>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 text-center border-0 bg-light">
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <i className="fas fa-skiing fa-4x text-primary"></i>
                  </div>
                  <h5 className="mb-3">Ski Bags</h5>
                  <p className="text-muted mb-3">
                    We can typically accept ski gear up to 220cm (and on some services larger still).
                  </p>
                  <a href="#" className="text-decoration-none fw-bold">Show More</a>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 text-center border-0 bg-light">
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <i className="fas fa-bicycle fa-4x text-primary"></i>
                  </div>
                  <h5 className="mb-3">Bicycles</h5>
                  <p className="text-muted mb-3">
                    We ship bikes every day but be sure to read our guidance on preparing your bike.
                  </p>
                  <a href="#" className="text-decoration-none fw-bold">Show More</a>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 text-center border-0 bg-light">
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <i className="fas fa-golf-ball fa-4x text-primary"></i>
                  </div>
                  <h5 className="mb-3">Golf Bags</h5>
                  <p className="text-muted mb-3">
                    No need to play with borrowed clubs ever again!
                  </p>
                  <a href="#" className="text-decoration-none fw-bold">Show More</a>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>

        {/* Not Allowed Section */}
        <section className="mb-5">
          <h2 className="mb-4">Not Allowed</h2>
          <Row>
            <Col md={6}>
              <Card className="border-0 bg-light">
                <Card.Body className="p-4">
                  <h5 className="mb-3">Restricted</h5>
                  <p className="text-muted">
                    Sorry, we can't accept plastic bags (including laundry bags) or plastic boxes. 
                    We also can't accept lithium batteries or aerosols, which we can't accept inside luggage. 
                    Our rules on toiletries vary by route.{' '}
                    <a href="/destinations">view our prohibited items list for your destination.</a>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>
      </Container>
    </div>
  );
}

export default SelectRoute;
